import BaseExportStrategy from './BaseExportStrategy';

/**
 * Estrategia de exportación para reportes de ANR
 * Define las columnas y el formato específico para ANR
 */
export default class ANRExportStrategy extends BaseExportStrategy {
  constructor() {
    super('ANR');
  }

  getTitle(context) {
    return 'INFORME DE ANR';
  }

  /**
   * Define las columnas del reporte de ANR
   */
  getColumns(options = {}) {
    const columns = [];

    // 1. Legajo
    columns.push({
      key: 'legajo',
      header: 'Legajo',
      pdfWidth: 15,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.id || '-',

      formatSubtotal: (item, ctx) => 'SUBTOTAL',

      formatTotal: (items, ctx) => ''  // Vacío - el texto va en Fideicomiso
    });

    // 2. Fideicomiso
    columns.push({
      key: 'fideicomiso',
      header: 'Fideicomiso',
      pdfWidth: 30,
      excelWidth: 30,
      align: 'left',

      format: (item, ctx) => item.fideicomisoName || '-',

      formatSubtotal: (item, ctx) => `${item._cantidadRegistros || 0} registros`,

      formatTotal: (items, ctx) => 'TOTAL - RESUMEN'  // Texto aquí
    });

    // 3. Operatoria / Línea
    columns.push({
      key: 'operatoria',
      header: 'Operatoria / Línea',
      pdfWidth: 38,
      excelWidth: 35,
      align: 'left',

      format: (item, ctx) => item.operatoria || '-',

      formatSubtotal: (item, ctx) => {
        return (item._subtotales && item._subtotales.operatoria) || item.operatoria || '-';
      },

      formatTotal: (items, ctx) => ''
    });

    // 4. Apellido y Nombre / Razón Social
    columns.push({
      key: 'cliente',
      header: 'Apellido y Nombre / Razón Social',
      pdfWidth: 40,
      excelWidth: 40,
      align: 'left',

      format: (item, ctx) => {
        if (!item.cliente) return '-';
        return (item.cliente.razonSocial && item.cliente.razonSocial.toUpperCase()) ||
               (item.cliente.apellido + ' ' + item.cliente.nombre).trim().toUpperCase() ||
               '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 5. CUIT
    columns.push({
      key: 'cuit',
      header: 'CUIT',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => (item.cliente && item.cliente.cuit) || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 6. Departamento
    columns.push({
      key: 'departamento',
      header: 'Departamento',
      pdfWidth: 25,
      excelWidth: 20,
      align: 'left',

      format: (item, ctx) => {
        // ✅ PRIMERO: Verificar si el departamento ya viene del backend
        if (item.departamento && item.departamento !== '') {
          return item.departamento;
        }
        // Intentar obtener departamento desde diferentes estructuras
        if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
          return item.localidad.departamento.nombre;
        }
        if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
          return item.cliente.localidad.departamento.nombre;
        }
        // Fallback a localidad si no hay departamento
        if (item.cliente && item.cliente.localidad) {
          return item.cliente.localidad;
        }
        if (item.localidad && typeof item.localidad === 'string') {
          return item.localidad;
        }
        if (item.localidad && item.localidad.nombre) {
          return item.localidad.nombre;
        }
        return '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 7. Persona
    columns.push({
      key: 'persona',
      header: 'Persona',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => {
        if (!item.cliente) return '-';
        return item.cliente.tipoCliente === 0 ? 'Jurídica' : 'Física';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 8. Fecha de Ingreso
    columns.push({
      key: 'fechaIngreso',
      header: 'Fecha de Ingreso',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fechaIngreso),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 9. Estado
    columns.push({
      key: 'estado',
      header: 'Estado',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.estado || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 10. Monto Solicitado (ANR usa monto_total_solicitado)
    columns.push({
      key: 'monto_total_solicitado',
      header: 'Monto Solicitado',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = this.getTipoMonto(item.parametros, 'monto_total_solicitado');
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.monto) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = this.getTipoMonto(item.parametros, 'monto_total_solicitado');
          return total + parseFloat(monto);
        }, 0);
      }
    });

    // 11. Monto Aprobado
    columns.push({
      key: 'monto_aprobado',
      header: 'Monto Aprobado',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = this.getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.montoAprobado) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = this.getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;
          return total + parseFloat(monto);
        }, 0);
      }
    });

    // 12. Monto Otorgado (para ANR, suma de desembolsos)
    columns.push({
      key: 'monto',
      header: 'Monto Otorgado',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        // Para ANR, sumar todos los desembolsos
        if (item.desembolsos && Array.isArray(item.desembolsos)) {
          const montoOtorgado = item.desembolsos.reduce((total, desembolso) => {
            return total + (parseFloat(desembolso.monto) || 0);
          }, 0);
          return this.formatMonto(montoOtorgado);
        }
        return this.formatMonto(0);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.monto) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          if (item.desembolsos && Array.isArray(item.desembolsos)) {
            const montoOtorgado = item.desembolsos.reduce((sum, desembolso) => {
              return sum + (parseFloat(desembolso.monto) || 0);
            }, 0);
            return total + montoOtorgado;
          }
          return total;
        }, 0);
      }
    });

    // 13. Monto Pendiente
    columns.push({
      key: 'monto_pendiente',
      header: 'Monto Pendiente',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = this.getPendienteDesembolso(item);
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.montoPendiente) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const montoPendiente = this.getPendienteDesembolso(item);
          return total + parseFloat(montoPendiente);
        }, 0);
      }
    });

    // 14-N. Desembolsos dinámicos
    const maxDesembolsos = this.getMaxDesembolsos(options);
    console.log(`📊 ANR - Generando ${maxDesembolsos} columnas de desembolsos`);

    for (let i = 1; i <= maxDesembolsos; i++) {
      // Monto del desembolso
      columns.push({
        key: `desembolso_${i}`,
        header: `Desembolso ${i}`,
        pdfWidth: 22,
        excelWidth: 16,
        align: 'right',

        format: (item, ctx) => {
          const desembolso = this.getDesembolso(item, i);
          if (desembolso) {
            return this.formatMonto(desembolso.monto);
          }
          return '-';
        },

        formatSubtotal: (item, ctx) => {
          const value = (item._subtotales && item._subtotales.desembolsos && item._subtotales.desembolsos[i]) || 0;
          return this.formatMonto(value);
        },

        calculateTotal: (items, ctx) => {
          return items.reduce((total, item) => {
            const desembolso = this.getDesembolso(item, i);
            if (desembolso) {
              return total + parseFloat(desembolso.monto || 0);
            }
            return total;
          }, 0);
        }
      });

      // Fecha del desembolso
      columns.push({
        key: `fecha_desembolso_${i}`,
        header: `Fecha Desembolso ${i}`,
        pdfWidth: 22,
        excelWidth: 16,
        align: 'left',

        format: (item, ctx) => {
          const desembolso = this.getDesembolso(item, i);
          if (desembolso && desembolso.fecha_desembolso) {
            return desembolso.fecha_desembolso;
          }
          return '-';
        },

        formatSubtotal: (item, ctx) => '',

        formatTotal: (items, ctx) => ''
      });
    }

    return columns;
  }

  /**
   * Obtiene el número máximo de desembolsos de los datos
   */
  getMaxDesembolsos(options) {
    // Obtener del contexto si está disponible
    if (options.maxDesembolsos !== undefined) {
      console.log(`✅ ANR - Usando maxDesembolsos del contexto: ${options.maxDesembolsos}`);
      return options.maxDesembolsos;
    }

    // Por defecto, 7 desembolsos
    console.warn(`⚠️ ANR - No se encontró maxDesembolsos en options, usando default: 7`);
    console.log('options:', options);
    return 7;
  }

  /**
   * Calcula el monto pendiente de desembolso para ANR
   * OVERRIDE: Para ANR, el monto otorgado es la suma de desembolsos, no item.parametros.monto
   * @param {Object} item
   * @returns {Number}
   */
  getPendienteDesembolso(item) {
    const montoAprobado = this.getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;

    // Para ANR, calcular monto otorgado como suma de desembolsos
    let montoOtorgado = 0;
    if (item.desembolsos && Array.isArray(item.desembolsos)) {
      montoOtorgado = item.desembolsos.reduce((total, desembolso) => {
        return total + (parseFloat(desembolso.monto) || 0);
      }, 0);
    }

    return montoAprobado - montoOtorgado;
  }
}
